<script setup>
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import TooltipQuestion from "@/components/global-themed/TooltipQuestion.vue";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import {computed} from "vue";
import FadeTransition from "@/components/transitions/FadeTransition.vue";
import CrawlerStatus from "@/components/actions/partials/settings/CrawlerStatus.vue";
import {crawlInProgress, recrawlMutation, crawlStatusQuery, crawlLogQuery} from "@/composables/crawler/useCrawler";

const props = defineProps({
  websiteId: {
    required: true,
    type: Number,
  },
});

const websiteId = computed(() => props.websiteId);

const { data: crawlLog, isLoading } = crawlLogQuery();
const { mutate: recrawl, isPending } = recrawlMutation();
const { data: currentStageText, isLoading: crawlStatusLoading } = crawlStatusQuery(websiteId);

const nextCrawlTooltip = computed(() => `We crawl your site monthly. The next crawl is scheduled for ${crawlLog.value.nextCrawl}. Click ‘Recrawl’ above if you need it sooner.`);
</script>

<template>
  <div class="tw-bg-white tw-p-8 tw-rounded-lg tw-flex tw-flex-col tw-gap-y-8 tw-w-full tw-h-[400px]">
    <p class="tw-font-bold tw-text-2.5xl">
      Crawl Status
    </p>

    <LoadingBlock
      v-if="isLoading || crawlStatusLoading"
      class="tw-h-44"
    />

    <div
      v-else
      class="tw-rounded-lg tw-border tw-border-grey-mid tw-flex tw-p-8 tw-justify-between tw-h-62 tw-gap-x-8"
    >
      <div class="tw-flex tw-flex-col tw-gap-16 tw-justify-between tw-flex-shrink-0">
        <div class="tw-flex tw-flex-col tw-gap-y-2">
          <span class="tw-text-base tw-text-grey">Last Audit</span>
          <span class="tw-font-bold tw-text-xl tw-text-grey-dark tw-inline-flex">
            {{ crawlLog.lastCrawl ?? '-' }}
            <TooltipQuestion
              class="tw-ml-2"
              text="This is the date we last crawled your website."
            />
          </span>
        </div>

        <div class="tw-flex tw-flex-col tw-gap-y-2">
          <span class="tw-text-base tw-text-grey">Next Crawl</span>
          <span class="tw-font-bold tw-text-xl tw-text-grey-dark tw-inline-flex">
            {{ crawlLog.nextCrawl ?? '-' }}
            <TooltipQuestion
              class="tw-ml-2"
              :text="nextCrawlTooltip"
            />
          </span>
        </div>
      </div>

      <FadeTransition>
        <DefaultButton
          v-if="!crawlInProgress"
          variant="primary-inverted"
          class="tw-h-13 tw-font-bold"
          :disabled="isPending"
          @click="recrawl(websiteId)"
        >
          <svg-vue
            icon="icons/refresh"
            class="tw-w-6 tw-h-6 tw-mr-3"
            :class="{
              'tw-animate-spin' : isPending,
            }"
          />
          Recrawl
        </DefaultButton>

        <CrawlerStatus
          v-else
          :current-stage-text="currentStageText"
        />
      </FadeTransition>
    </div>
  </div>
</template>
